/**
 * @file Settings page — editable resale formula coefficients (laptops + phones)
 * and a read-only display of the hardcoded admin allowlist.
 *
 * The formula editor saves to /config/resaleFormulas. The offboarding pages
 * read it on each render so changes propagate without a deploy.
 */

import { el, clear, formatDate, toast } from '../utils.js';
import { Icons } from '../icons.js';
import {
  getConfig, setConfig, watchConfig,
  watchAdmins,
} from '../db.js';
import { defaultFormulas } from '../formula.js';
import { renderSyncButton } from '../sync-button.js';
import { renderPageHead } from '../page-header.js';
import { syncBambooAssets, testSlack, getSlackSettings, saveSlackSettings } from '../api.js';
import { SYNC_STAGES, buildSyncAuditDetails, runStage, runExclusive, isSyncBusy, onSyncBusyChange, SyncBusyError } from '../lib/sync-stages.js';
import { isTestMode } from '../test-mode.js';
import { getCurrentUser } from '../auth.js';
import { toggleTestMode, signOutHere } from '../session-actions.js';
import { logAuditEvent }  from '../audit-log.js';
import { AUDIT_ACTION }   from '../lib/audit-actions.js';

export async function renderSettings(host) {
  clear(host);

  host.appendChild(renderPageHead('Settings', 'Editable runtime configuration.'));

  // ── Session (Test mode + Sign out — the profile menu's, which the IT Hub hides)
  host.appendChild(renderSessionCard());

  // ── Sync panel (top of page) ──────────────────────────────────────────
  host.appendChild(renderSyncCard(host));

  // ── Slack notifications ──────────────────────────────────────────────
  host.appendChild(renderSlackCard());

  const formulas = (await getConfig('resaleFormulas')) || defaultFormulas();

  // ── Resale formula editor ───────────────────────────────────────────────
  const formulaCard = el('section', { class: 'card' },
    el('h2', { class: 'heading' }, 'Resale value formula'),
    el('p', { class: 'muted' },
      'Used during offboarding to suggest a buy-back price. For the first "grace" months the device is valued at full price (STRV doesn\'t sell it yet); at the end of grace the value drops to the given % of purchase price, then falls linearly to the residual floor by the end of the depreciation period. Grace 0 at 100% = plain linear (laptops). Per-model overrides match when every word of the override appears somewhere in the device name (any order, case/diacritics-insensitive). The first matching row wins, so order specific rules above generic ones — use ↑/↓ to reorder.'),
  );

  const renderFormulaSection = (key) => {
    const f = formulas[key] || defaultFormulas()[key];
    const monthsInput   = el('input', { type: 'number', min: '1', value: String(f.depreciationMonths) });
    const residualInput = el('input', { type: 'number', min: '0', value: String(f.residualValue) });
    // Grace + cliff (the phones model — see src/formula.js). Grace 0 with
    // 100% = the plain laptop line, so both categories share one editor.
    const graceInput    = el('input', { type: 'number', min: '0', value: String(f.gracePeriodMonths ?? 0) });
    const sellableInput = el('input', { type: 'number', min: '1', max: '100', value: String(f.sellableAtPercent ?? 100) });
    const overridesHost = el('div', { class: 'stack', style: 'gap:8px' });

    const renderOverrides = (list) => {
      clear(overridesHost);
      list.forEach((row, i) => overridesHost.appendChild(renderOverrideRow(row, i, list, () => renderOverrides(list))));
      overridesHost.appendChild(el('button', {
        class: 'btn btn-sm',
        style: 'align-self:flex-start',
        onClick: () => {
          list.push({ match: '', residualValue: f.residualValue });
          renderOverrides(list);
        },
      }, Icons.plus(), 'Add override'));
    };
    renderOverrides([...(f.modelOverrides || [])]);

    const save = el('button', {
      class: 'btn btn-primary',
      onClick: async () => {
        const before = formulas[key] || {};
        const after = {
          label: f.label,
          depreciationMonths: Number(monthsInput.value) || f.depreciationMonths,
          residualValue:      Number(residualInput.value) || f.residualValue,
          gracePeriodMonths:  Math.max(0, Number(graceInput.value) || 0),
          sellableAtPercent:  Math.min(100, Math.max(1, Number(sellableInput.value) || 100)),
          modelOverrides:     extractOverrides(overridesHost),
        };
        if (after.gracePeriodMonths >= after.depreciationMonths) {
          toast('Grace period must be shorter than the depreciation period.', 'error');
          return;
        }
        try {
          await setConfig('resaleFormulas', { ...formulas, [key]: after });
          // Audit: snapshot the BEFORE + AFTER values so we can see how
          // the formula evolved and replay the change if needed.
          logAuditEvent({
            action: AUDIT_ACTION.SYSTEM_CONFIG_CHANGE,
            subject: { type: 'config', id: `resaleFormulas/${key}`, label: `${f.label} resale formula` },
            details: {
              path:   `resaleFormulas.${key}`,
              before: { depreciationMonths: before.depreciationMonths ?? null, residualValue: before.residualValue ?? null, gracePeriodMonths: before.gracePeriodMonths ?? null, sellableAtPercent: before.sellableAtPercent ?? null, modelOverrides: before.modelOverrides || [] },
              after:  { depreciationMonths: after.depreciationMonths,            residualValue: after.residualValue,            gracePeriodMonths: after.gracePeriodMonths,          sellableAtPercent: after.sellableAtPercent,          modelOverrides: after.modelOverrides },
            },
          });
          toast(`${f.label} formula saved.`, 'success');
        } catch (err) {
          console.error(err);
          toast(err.message || 'Save failed.', 'error');
        }
      },
    }, 'Save');

    return el('div', { class: 'stack', style: 'margin-top:24px' },
      el('h3', null, f.label),
      el('div', { class: 'grid grid-2' },
        el('div', { class: 'field' }, el('label', null, 'Depreciation months'),  monthsInput),
        el('div', { class: 'field' }, el('label', null, 'Residual value (CZK)'), residualInput),
        el('div', { class: 'field' }, el('label', null, 'Grace months (full price, not sold)'), graceInput),
        el('div', { class: 'field' }, el('label', null, 'Value after grace (% of price)'), sellableInput),
      ),
      el('div', { class: 'field' }, el('label', null, 'Per-model overrides'), overridesHost),
      el('div', { class: 'row', style: 'justify-content:flex-end' }, save),
    );
  };

  formulaCard.appendChild(renderFormulaSection('laptop'));
  formulaCard.appendChild(el('div', { class: 'divider' }));
  formulaCard.appendChild(renderFormulaSection('phone'));
  host.appendChild(formulaCard);

  host.appendChild(renderAdminsCard(host));
}

// ── Admins card (read-only) ───────────────────────────────────────────────
//
// Who administers Gear is decided in the IT Hub and mirrored into /admins
// by hubPermissions (every 15 minutes and on change). Listed here so admins
// can see who else has access.
function renderAdminsCard(host) {
  const card = el('section', { class: 'card', style: 'margin-top:24px' },
    el('h2', { class: 'heading' }, 'Admins'),
    el('p', { class: 'muted' }, 'Managed in IT Hub.'),
  );

  const listHost = el('ul', { style: 'margin-top:16px;list-style:none;padding:0;display:flex;flex-direction:column;gap:8px' });
  listHost.appendChild(el('p', { class: 'muted' }, 'Loading…'));
  card.appendChild(listHost);
  card.appendChild(el('div', { class: 'row', style: 'margin-top:16px' },
    el('a', { class: 'btn', href: 'https://ithub.strv.io', target: '_blank', rel: 'noopener' }, 'Open IT Hub')));

  const unsub = watchAdmins((admins) => {
    clear(listHost);
    if (!admins.length) {
      listHost.appendChild(el('p', { class: 'muted' }, 'No admins on file.'));
      return;
    }
    const me = getCurrentUser()?.email?.toLowerCase() || '';
    const sorted = [...admins].sort((a, b) => (a.email || '').localeCompare(b.email || ''));
    for (const a of sorted) {
      listHost.appendChild(el('li', { class: 'row', style: 'gap:12px;padding:8px 0;border-bottom:1px solid var(--color-surface-pressed)' },
        el('span', { class: 'chip' }, a.email === me ? 'You' : 'Admin'),
        el('div', { style: 'flex:1' },
          el('div', null, a.displayName || a.email),
          el('div', { class: 'muted', style: 'font-size:var(--fs-small)' },
            `${a.email}${a.addedAt ? ` · since ${formatDate(a.addedAt)}` : ''}`),
        ),
      ));
    }
  });
  host.addEventListener('cleanup', unsub, { once: true }); // fires on #page, not the card

  return card;
}

function renderOverrideRow(row, index, list, onChange) {
  const matchInput    = el('input', { type: 'text',  value: row.match || '',           placeholder: 'e.g. macbook air M1' });
  const residualInput = el('input', { type: 'number', value: String(row.residualValue || 0) });
  matchInput.addEventListener('input', () => { row.match = matchInput.value; });
  residualInput.addEventListener('input', () => { row.residualValue = Number(residualInput.value) || 0; });

  // Order IS precedence (first matching row wins), so rows can be moved.
  // Swap in the backing list and re-render; extractOverrides reads DOM order.
  const move = (delta) => {
    const j = index + delta;
    if (j < 0 || j >= list.length) return;
    [list[index], list[j]] = [list[j], list[index]];
    onChange();
  };
  const upBtn   = el('button', { class: 'btn btn-ghost btn-sm', title: 'Move up (higher precedence)',  onClick: () => move(-1) }, '↑');
  const downBtn = el('button', { class: 'btn btn-ghost btn-sm', title: 'Move down (lower precedence)', onClick: () => move(1) }, '↓');
  if (index === 0) upBtn.disabled = true;
  if (index === list.length - 1) downBtn.disabled = true;

  return el('div', { class: 'row', dataset: { row: 'true' } },
    el('span', { class: 'muted', style: 'width:22px;text-align:right;font-size:var(--fs-small)' }, String(index + 1)),
    el('div', { style: 'flex:1' }, matchInput),
    el('div', { style: 'flex:0 0 160px' }, residualInput),
    upBtn, downBtn,
    el('button', {
      class: 'btn btn-ghost btn-sm',
      title: 'Remove',
      onClick: () => { list.splice(index, 1); onChange(); },
    }, Icons.close()),
  );
}

function extractOverrides(host) {
  const rows = host.querySelectorAll('[data-row="true"]');
  const out = [];
  rows.forEach((row) => {
    const inputs = row.querySelectorAll('input');
    const match = inputs[0].value.trim();
    const residual = Number(inputs[1].value);
    if (match && Number.isFinite(residual)) out.push({ match, residualValue: residual });
  });
  return out;
}

// ── Sync card ──────────────────────────────────────────────────────────
//
// Two buttons here:
//   • Sync everything — runs the full pipeline in order:
//       1. BambooHR people list (one custom-report call)
//       2. Abra Flexi devices (paginated /majetek)
//       3. Meraki MDM device inventory
//       4. QA "Testing devices" Google Sheet (every tab)
//       5. Materialise — joins the four raw mirrors into /devices, flags
//          cross-system mismatches (Meraki vs. Abra, Testing sheet vs. Abra)
//       6. Joining-form Google Sheet — matches new-hire submissions to
//          BambooHR employees, materialises ordered items into onboarding.
//     The nightly cron at 02:00 Europe/Prague does the exact same thing.
//   • Sync phone tariffs — per-employee BambooHR customAssets fan-out. This
//     is the only path that updates phone-tariff state on /me. Kept
//     separate because it's an expensive ~300-call burst we only want when
//     it's actually needed.
function renderSyncCard(host) {
  const card = el('section', { class: 'card', style: 'margin-bottom:24px' },
    el('h2', { class: 'heading' }, 'Sync'),
    el('p', { class: 'muted' },
      "Gear mirrors BambooHR (people), Abra Flexi (devices), Meraki MDM, IRU MDM, and the QA testing-devices Google Sheet nightly at 02:00 Europe/Prague. Joining-form responses are pulled in the same run. Phone tariffs (Bamboo customAssets) refresh only when you ask — that's the expensive per-employee fan-out."),
  );

  // ── Status lines — one per registry stage that reports to /config/sync ──
  const statusEls = new Map(); // stage.key → <p>
  const statusStack = el('div', { class: 'stack', style: 'gap:4px;margin-bottom:12px' });
  for (const stage of SYNC_STAGES) {
    if (!stage.status) continue; // e.g. the testing sheet writes no status
    const line = el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin:0' },
      `${stage.status.label} — loading status...`);
    statusEls.set(stage.key, line);
    statusStack.appendChild(line);
  }
  const assetsStatus = el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin:0' }, 'Phone tariffs — loading status...');
  statusStack.appendChild(assetsStatus);

  const syncEverythingButton = renderSyncButton({ size: 'lg' });
  const assetsButton = el('button', { class: 'btn' }, 'Sync phone tariffs');
  let busy = false;
  assetsButton.addEventListener('click', async () => {
    if (busy || isSyncBusy()) return;
    busy = true;
    assetsButton.disabled = true;
    const originalText = assetsButton.textContent;
    assetsButton.textContent = 'Syncing phone tariffs…';
    try {
      const res = await runExclusive(() => syncBambooAssets());
      logAuditEvent({
        action: AUDIT_ACTION.SYSTEM_ASSETS_SYNC,
        details: {
          totalFetched:       res?.totalFetched ?? null,
          skippedDoneNoTariff: res?.skippedDoneNoTariff ?? null,
          updated:            res?.updated ?? null,
          withTariff:         res?.withTariff ?? null,
          durationMs:         res?.durationMs ?? null,
          errors:             res?.errors ?? 0,
        },
      });
      toast(`Updated ${res?.updated ?? 0} employees · ${res?.withTariff ?? 0} have a tariff.`, 'success');
    } catch (err) {
      if (err instanceof SyncBusyError) { toast(err.message, 'info'); }
      else {
        console.error('[settings] syncBambooAssets', err);
        toast(err.message || 'Sync failed.', 'error');
      }
    } finally {
      busy = false;
      assetsButton.disabled = isSyncBusy();
      assetsButton.textContent = originalText;
    }
  });

  // ── Individual source buttons — one per registry stage ──────────────────
  // For verifying a single source after a change without waiting on the
  // full chain. Same test-mode rule as Sync everything: these hit the live
  // external systems, so they're disabled in the sandbox.
  const singleButtons = SYNC_STAGES.map((stage) => {
    const btn = el('button', { class: 'btn btn-sm' }, stage.buttonLabel);
    if (isTestMode()) {
      btn.disabled = true;
      btn.title = 'Disabled in test mode — syncs run against live systems, not the sandbox.';
      return btn;
    }
    btn.addEventListener('click', async () => {
      if (btn.disabled || isSyncBusy()) return;
      btn.disabled = true;
      const original = stage.buttonLabel;
      btn.textContent = 'Syncing…';
      try {
        // runStage holds the tab-wide sync lock for the source AND its
        // chained materialise, so mashing other buttons meanwhile no-ops.
        const { result, ranStages } = await runStage(stage, {
          onPhase: (phase) => { btn.textContent = phase === 'join' ? 'Joining…' : 'Syncing…'; },
        });
        // Same audit action + per-source diff shape as the full run, so the
        // audit-log renderer shows exactly the sections that ran.
        logAuditEvent({
          action: AUDIT_ACTION.SYSTEM_SYNC_RAN,
          details: { source: 'manual', stage: stage.key, ...buildSyncAuditDetails(result, ranStages) },
        });
        if (result.errors.length) {
          const msg = result.errors[0]?.error || '';
          toast(msg.includes('already running')
            ? `${stage.buttonLabel} synced. Another materialise join is running right now — it will pick these changes up.`
            : `${stage.buttonLabel} synced, but the materialise join failed — the app view may be stale. Check the console.`,
            msg.includes('already running') ? 'info' : 'error', 8000);
        } else {
          const frags = ranStages.map((x) => (result[x.key] ? x.toastFragment(result[x.key]) : null)).filter(Boolean);
          toast(`${stage.status?.label || stage.buttonLabel} synced${frags.length ? ` — ${frags.join(', ')}` : ''}.`, 'success');
        }
      } catch (err) {
        if (err instanceof SyncBusyError) { toast(err.message, 'info'); }
        else {
          console.error(`[settings] sync ${stage.key}`, err);
          toast(err.message || `${stage.buttonLabel} sync failed.`, 'error');
        }
      } finally {
        btn.disabled = isSyncBusy(); // stay grey if another sync still runs
        btn.textContent = original;
      }
    });
    return btn;
  });

  // Any sync in flight anywhere in the tab greys out every sync control
  // here — this is what makes "press four buttons at once" a no-op.
  const allSyncButtons = [...singleButtons, assetsButton];
  const unsubBusy = onSyncBusyChange((anyBusy) => {
    if (isTestMode()) return; // test-mode buttons are permanently disabled
    for (const b of allSyncButtons) {
      if (anyBusy) { b.disabled = true; b.title = 'Another sync is already running.'; }
      else if (b.textContent !== 'Syncing…' && b.textContent !== 'Joining…') { b.disabled = false; b.title = ''; }
    }
  });
  host.addEventListener('cleanup', unsubBusy, { once: true });

  card.appendChild(el('div', { class: 'divider' }));
  card.appendChild(statusStack);
  card.appendChild(el('div', { class: 'row', style: 'gap:8px' }, syncEverythingButton, assetsButton));
  card.appendChild(el('div', { class: 'label', style: 'margin-top:16px' }, 'Sync one source'));
  card.appendChild(el('div', { class: 'row', style: 'gap:8px;flex-wrap:wrap;margin-top:8px' }, ...singleButtons));
  card.appendChild(el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin:8px 0 0' },
    'Source buttons re-run the Materialise join automatically, so changes show up in the app right away. The standalone Materialise button re-joins without pulling anything (it also runs pending-change reconciliation).'));

  const unsub = watchConfig('sync', (cfg) => {
    for (const stage of SYNC_STAGES) {
      const elLine = statusEls.get(stage.key);
      if (!elLine) continue;
      elLine.textContent = stage.status.line
        ? stage.status.line(cfg?.[stage.key])
        : formatStatus(stage.status.label, cfg?.[stage.key], stage.status.noun);
    }
    assetsStatus.textContent = formatAssetsStatus(cfg?.bamboo);
  });
  host.addEventListener('cleanup', unsub, { once: true }); // fires on #page, not the card

  return card;
}

// ── Session card ───────────────────────────────────────────────────────
function renderSessionCard() {
  const testBtn = el('button', { class: 'btn' }, isTestMode() ? 'Leave test mode' : 'Enter test mode');
  testBtn.addEventListener('click', async () => {
    testBtn.disabled = true;
    try { await toggleTestMode(); }
    catch (err) { toast(err.message || 'Could not enter test mode.', 'error'); testBtn.disabled = false; }
  });
  const signOutBtn = el('button', { class: 'btn btn-ghost', onClick: signOutHere }, 'Sign out');
  return el('section', { class: 'card', style: 'margin-bottom:24px' },
    el('h2', { class: 'heading' }, 'Session'),
    el('p', { class: 'muted' }, getCurrentUser()?.email || ''),
    el('div', { class: 'row', style: 'gap:8px;margin-top:12px' }, testBtn, signOutBtn),
  );
}

// ── Slack card ─────────────────────────────────────────────────────────
//
// The incoming-webhook Gear uses to ping Finance when an admin submits a
// device action. The URL is a credential: it lives in Secret Manager and
// only ever comes back masked. Leaving the field empty keeps the current
// one. `destinationLabel` lands in audit rows so we can see where a message
// went (Slack doesn't echo the channel back).
function renderSlackCard() {
  const urlInput = el('input', { type: 'url', placeholder: 'https://hooks.slack.com/services/…', style: 'flex:1', autocomplete: 'off' });
  const labelInput = el('input', { type: 'text', placeholder: '#it-handovers', style: 'flex:1' });
  const enabledCheckbox = el('input', { type: 'checkbox' });
  const enabledLabel = el('label', { style: 'display:inline-flex;gap:6px;align-items:center' },
    enabledCheckbox, el('span', null, 'Send messages'));

  const saveBtn = el('button', { class: 'btn btn-primary' }, 'Save');
  const testBtn = el('button', { class: 'btn' }, 'Send test');
  const status = el('p', { class: 'muted', style: 'font-size:var(--fs-small);margin:0' }, 'Loading…');
  const controls = [urlInput, labelInput, enabledCheckbox, saveBtn, testBtn];

  const show = (cfg) => {
    urlInput.value = '';
    urlInput.placeholder = cfg.webhookMasked || 'https://hooks.slack.com/services/…';
    labelInput.value = cfg.destinationLabel || '';
    enabledCheckbox.checked = cfg.enabled !== false;
    status.textContent = !cfg.configured ? 'Not configured.'
      : cfg.enabled === false ? 'Configured · off.'
      : `Configured · ${cfg.destinationLabel || 'no label'}.`;
  };

  const load = async () => {
    if (isTestMode()) {
      for (const c of controls) c.disabled = true;
      status.textContent = 'Production only.';
      return;
    }
    try { show(await getSlackSettings()); }
    catch (err) { console.error('[settings] load slack config', err); status.textContent = 'Could not load.'; }
  };

  saveBtn.addEventListener('click', async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving…';
    try {
      await saveSlackSettings({
        webhookUrl:       urlInput.value.trim() || null,
        destinationLabel: labelInput.value.trim() || null,
        enabled:          enabledCheckbox.checked,
      });
      toast('Saved.', 'success');
      await load();
    } catch (err) {
      console.error('[settings] save slack config', err);
      toast(err.message || 'Save failed.', 'error');
    } finally {
      saveBtn.disabled = isTestMode();
      saveBtn.textContent = 'Save';
    }
  });

  testBtn.addEventListener('click', async () => {
    testBtn.disabled = true;
    testBtn.textContent = 'Sending…';
    try {
      const res = await testSlack();
      if (res?.ok) toast('Test sent. Check Slack.', 'success');
      else toast(res?.error || res?.reason || 'Slack rejected the message.', 'error');
    } catch (err) {
      toast(err.message || 'Test failed.', 'error');
    } finally {
      testBtn.disabled = isTestMode();
      testBtn.textContent = 'Send test';
    }
  });

  const card = el('section', { class: 'card', style: 'margin-bottom:24px' },
    el('h2', { class: 'heading' }, 'Slack notifications'),
    el('p', { class: 'muted' },
      'Device actions post here so Finance can do their part (handover document, invoice, Abra).'),
    el('div', { class: 'stack', style: 'gap:12px;margin-top:12px' },
      el('div', { class: 'row', style: 'gap:8px;align-items:center' },
        el('label', { class: 'label', style: 'min-width:140px' }, 'Webhook URL'),
        urlInput),
      el('div', { class: 'row', style: 'gap:8px;align-items:center' },
        el('label', { class: 'label', style: 'min-width:140px' }, 'Destination label'),
        labelInput),
      el('div', { class: 'row', style: 'gap:8px;align-items:center' },
        el('span', { class: 'label', style: 'min-width:140px' }, 'Active'),
        enabledLabel),
      status,
      el('div', { class: 'row', style: 'gap:8px;margin-top:8px' }, saveBtn, testBtn),
    ),
  );

  load();
  return card;
}

function formatStatus(label, s, countLabel) {
  if (!s || !s.lastSucceededAt) return `${label} — never synced.`;
  const when  = formatDate(s.lastSucceededAt);
  const count = s.deviceCount ?? s.employeeCount ?? s.submissionCount ?? '-';
  const errors = s.summary?.errors ?? 0;
  return `${label} — last list sync ${when}, ${count} ${countLabel}${errors ? `, ${errors} errors` : ''}.`;
}

function formatAssetsStatus(bamboo) {
  if (!bamboo?.lastAssetsSucceededAt) return 'Phone tariffs — never synced manually. Run on demand below.';
  const when = formatDate(bamboo.lastAssetsSucceededAt);
  const updated = bamboo.assetsSummary?.updated ?? 0;
  const tariffs = bamboo.assetsSummary?.withTariff ?? 0;
  return `Phone tariffs — last refresh ${when}, ${updated} employees scanned, ${tariffs} active tariffs.`;
}
